/** Reusable CMS image uploader — replaces "paste an image URL" fields.
 * Uploads to /api/media (stored in the database) and returns a served URL. */
import { useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Upload, X, Loader2 } from "lucide-react";

const MAX_MB = 4;

export const ImageUploadField = ({ value, onChange, label = "Feature image", testid = "image-upload-field" }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pick = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please choose an image file (JPG, PNG or WebP).");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Please choose a photo under ${MAX_MB}MB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      setUploading(true);
      try {
        const res = await api.post("/media", {
          image_base64: String(reader.result).split(",")[1],
          content_type: f.type,
          filename: f.name,
        });
        onChange(res.data.url);
        toast.success("Photo uploaded.");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload failed — please try again.");
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(f);
  };

  return (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#6B5944] mb-1.5">{label}</div>
      {value ? (
        <div className="relative rounded-xl overflow-hidden border border-[#EBE3D2] bg-white">
          <img src={value} alt="Feature" className="w-full max-h-44 object-cover" data-testid={`${testid}-preview`} />
          <div className="absolute top-2 right-2 flex gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="bg-[#2A1810]/80 text-[#FDFBF5] text-xs px-3 py-1.5 rounded-full hover:bg-[#2A1810] transition-colors"
              data-testid={`${testid}-replace`}
            >
              {uploading ? "Uploading…" : "Replace"}
            </button>
            <button
              type="button"
              onClick={() => onChange("")}
              aria-label="Remove image"
              className="bg-[#2A1810]/80 text-[#FDFBF5] p-1.5 rounded-full hover:bg-red-700 transition-colors"
              data-testid={`${testid}-remove`}
            >
              <X size={13} />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="w-full border-2 border-dashed border-[#C28A2E]/40 rounded-xl p-6 text-center hover:border-[#C28A2E] transition-colors disabled:opacity-60"
          data-testid={`${testid}-zone`}
        >
          <div className="text-[#6B5944] text-sm flex flex-col items-center gap-2">
            {uploading ? <Loader2 size={20} className="animate-spin text-[#C28A2E]" /> : <Upload size={20} className="text-[#C28A2E]" />}
            {uploading ? "Uploading…" : `Click to upload a photo (JPG/PNG, up to ${MAX_MB}MB)`}
          </div>
        </button>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          pick(e.target.files?.[0]);
          e.target.value = "";
        }}
        data-testid={`${testid}-input`}
      />
    </div>
  );
};

export default ImageUploadField;
